import React, { useEffect, useId, useRef } from 'react';
import IconButton from '../IconButton/IconButton';

function Drawer({
  children,
  className = '',
  labelledBy,
  onClose,
  open,
  placement = 'left',
  rootClassName = '',
  title,
  variant = 'temporary',
}) {
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const generatedTitleId = useId();
  const titleId = labelledBy || generatedTitleId;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open || variant === 'permanent') {
      return undefined;
    }

    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusFirstElement = () => {
      const firstFocusable = panelRef.current?.querySelector(focusableSelector);
      if (firstFocusable) {
        firstFocusable.focus();
        return;
      }

      panelRef.current?.focus();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.();
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusable = [...panelRef.current.querySelectorAll(focusableSelector)]
        .filter((element) => !element.disabled && element.offsetParent !== null);

      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    focusFirstElement();
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [open, variant]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={[
        'custom-drawer',
        `custom-drawer--${variant}`,
        `custom-drawer--${placement}`,
        rootClassName,
      ].filter(Boolean).join(' ')}
      role="presentation"
    >
      {variant === 'temporary' && (
        <button type="button" className="custom-drawer__overlay" aria-label="Fermer" onClick={onClose} />
      )}
      <aside
        ref={panelRef}
        className={[
          'custom-drawer__panel',
          `custom-drawer__panel--${placement}`,
          `custom-drawer__panel--${variant}`,
          className,
        ].filter(Boolean).join(' ')}
        role={variant === 'temporary' ? 'dialog' : 'navigation'}
        aria-modal={variant === 'temporary' ? 'true' : undefined}
        aria-label={!title ? 'Panneau latéral' : undefined}
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {(title || onClose) && (
          <header className="custom-drawer__header">
            {title && <strong id={titleId}>{title}</strong>}
            {onClose && (
              <IconButton className="custom-drawer__close" ariaLabel="Fermer" onClick={onClose} size="small">
                x
              </IconButton>
            )}
          </header>
        )}
        <div className="custom-drawer__content">{children}</div>
      </aside>
    </div>
  );
}

export default Drawer;
